import { AxiosResponse } from 'axios'
import axios from '@/services/axios'

export default abstract class SharedAccountsApi {
  public static route = process.env.VUE_APP_ARENA_HUB_BASE_URI
  public static entity = 'shared-accounts'

  // GET /arenas/{arena_id}/shared-accounts
  public static async list(
    arenaId: string | number,
    params = '',
  ): Promise<AxiosResponse> {
    return await axios.get(
      `${this.route}/arenas/${arenaId}/${this.entity}?${params}`,
    )
  }

  // POST /arenas/{arena_id}/shared-accounts  (admin only) -> 201
  // body holds password/secret - never log it. 201 returns the no-secrets shape.
  public static async create(
    arenaId: string | number,
    data: object,
  ): Promise<AxiosResponse> {
    return await axios.post(
      `${this.route}/arenas/${arenaId}/${this.entity}`,
      data,
    )
  }

  // PATCH /arenas/{arena_id}/shared-accounts/{shared_account_id} -> 200
  // PATCH semantics: only sent fields change; app_id & owner_id are immutable.
  // `banned` is an accepted field here (the dedicated /ban route is gone).
  // Roles: admin may change any fields; manager/employee may send ONLY { banned }
  // (any other field, or banned + others, returns 403).
  // body may hold password/secret when changed - never log it.
  public static async update(
    arenaId: string | number,
    sharedAccountId: string,
    data: object,
  ): Promise<AxiosResponse> {
    return await axios.patch(
      `${this.route}/arenas/${arenaId}/${this.entity}/${sharedAccountId}`,
      data,
    )
  }

  // DELETE /arenas/{arena_id}/shared-accounts/{shared_account_id}  (admin)
  // Soft delete -> 204 No Content (404 if not found).
  public static async remove(
    arenaId: string | number,
    sharedAccountId: string,
  ): Promise<AxiosResponse> {
    return await axios.delete(
      `${this.route}/arenas/${arenaId}/${this.entity}/${sharedAccountId}`,
    )
  }

  // GET /arenas/{arena_id}/shared-accounts/stats  roles: admin, manager
  // Required date-only `date_gte`/`date_lt` (YYYY-MM-DD), half-open window
  // [date_gte, date_lt). 400 on missing/bad format, date_lt<=date_gte, or
  // range>366d. Response echoes data_from/data_to + summary + by_game.
  public static async stats(
    arenaId: string | number,
    params = '',
  ): Promise<AxiosResponse> {
    return await axios.get(
      `${this.route}/arenas/${arenaId}/${this.entity}/stats?${params}`,
    )
  }

  // PATCH /arenas/{arena_id}/shared-accounts/{shared_account_id}/games/{game_id}
  // body: { banned_until: string | null }
  public static async updateGameBan(
    arenaId: string | number,
    sharedAccountId: string,
    gameId: string,
    data: { banned_until: string | null },
  ): Promise<AxiosResponse> {
    return await axios.patch(
      `${this.route}/arenas/${arenaId}/${this.entity}/${sharedAccountId}/games/${gameId}`,
      data,
    )
  }

  // POST /arenas/{arena_id}/shared-accounts/borrow
  // Idempotent per session. Returns { first, second } credential objects.
  // NOTE: the response contains password/secret - never log it.
  public static async borrow(
    arenaId: string | number,
    data: { app_id: string; game_id: string; session_id: string },
  ): Promise<AxiosResponse> {
    return await axios.post(
      `${this.route}/arenas/${arenaId}/${this.entity}/borrow`,
      data,
    )
  }

  // GET /arenas/{arena_id}/shared-accounts/{shared_account_id}/two-fa-code
  // 200 { code } once available (consumed on read, ~5 min validity); 404 = not yet.
  public static async twoFaCode(
    arenaId: string | number,
    sharedAccountId: string,
  ): Promise<AxiosResponse> {
    return await axios.get(
      `${this.route}/arenas/${arenaId}/${this.entity}/${sharedAccountId}/two-fa-code`,
    )
  }

  // POST /arenas/{arena_id}/shared-accounts/return  { session_id }
  // Releases ALL accounts borrowed on that session.
  public static async returnSession(
    arenaId: string | number,
    data: { session_id: string },
  ): Promise<AxiosResponse> {
    return await axios.post(
      `${this.route}/arenas/${arenaId}/${this.entity}/return`,
      data,
    )
  }

  // GET /arenas/{arena_id}/shared-accounts/{shared_account_id}/borrow-history
  // Paginated, newest-first borrow events. No secrets.
  public static async borrowHistory(
    arenaId: string | number,
    sharedAccountId: string,
    params = '',
  ): Promise<AxiosResponse> {
    return await axios.get(
      `${this.route}/arenas/${arenaId}/${this.entity}/${sharedAccountId}/borrow-history?${params}`,
    )
  }
}
